import { useCallback, useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string
      remove: (widgetId: string) => void
      reset: (widgetId?: string) => void
    }
  }
}

const RAW_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined

// Turnstile sitekeys are hostname-restricted; the prod key rejects localhost
// (error 110200). Skip the challenge in local dev so login isn't blocked.
const isLocalhost =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)

export const TURNSTILE_SITE_KEY = isLocalhost ? undefined : RAW_SITE_KEY

const SITE_KEY = TURNSTILE_SITE_KEY

export function useTurnstile() {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)
  const [token, setTokenState] = useState<string | null>(null)
  const [ready, setReady] = useState(!SITE_KEY) // if no site key, skip
  const tokenRef = useRef<string | null>(null)
  const waiters = useRef<Array<(t: string) => void>>([])

  const setToken = (t: string | null) => {
    tokenRef.current = t
    setTokenState(t)
    if (t) for (const w of waiters.current.splice(0)) w(t)
  }

  const reset = useCallback(() => {
    setToken(null)
    if (SITE_KEY && widgetId.current) {
      window.turnstile?.reset(widgetId.current)
    }
  }, [])

  // The check usually finishes in the background; give a slow one a few seconds
  // (or the visitor time to tick the box if Cloudflare asks) before giving up.
  const waitForToken = useCallback(
    (ms: number) =>
      new Promise<string | null>((resolve) => {
        if (tokenRef.current) return resolve(tokenRef.current)
        if (!SITE_KEY) return resolve(null)
        const timer = setTimeout(() => {
          waiters.current = waiters.current.filter((w) => w !== done)
          resolve(null)
        }, ms)
        const done = (t: string) => {
          clearTimeout(timer)
          resolve(t)
        }
        waiters.current.push(done)
      }),
    [],
  )

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return

    function mount() {
      if (!containerRef.current || widgetId.current) return
      widgetId.current = window.turnstile!.render(containerRef.current, {
        sitekey: SITE_KEY,
        // Stays invisible unless Cloudflare needs the visitor to tick a box;
        // a hidden widget could never show that, leaving them stuck.
        size: 'flexible',
        appearance: 'interaction-only',
        callback: (t: string) => {
          setToken(t)
          setReady(true)
        },
        'error-callback': () => {
          setReady(false)
          setTimeout(() => widgetId.current && window.turnstile?.reset(widgetId.current), 2000)
          return true
        },
        'expired-callback': () => {
          setToken(null)
          setReady(false)
        },
      })
    }

    if (window.turnstile) {
      mount()
    } else {
      // Load script if not present
      if (!document.getElementById('cf-turnstile-script')) {
        const s = document.createElement('script')
        s.id = 'cf-turnstile-script'
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
        s.async = true
        s.onload = mount
        document.head.appendChild(s)
      } else {
        const check = setInterval(() => {
          if (window.turnstile) {
            clearInterval(check)
            mount()
          }
        }, 100)
      }
    }

    return () => {
      if (widgetId.current) {
        window.turnstile?.remove(widgetId.current)
        widgetId.current = null
      }
    }
  }, [])

  return { containerRef, token, ready, reset, waitForToken }
}
